import React, { useEffect } from 'react';
import {
	MenuList,
	MenuListItem,
	MenuSection,
	MenuSectionHeading,
} from '@atlaskit/navigation-system';
import { useIntl } from '@atlassian/jira-intl';
import { MENU_ID_PROJECTS_VIEW_ALL_STARRED } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ProjectsDroppableGroupIndicator } from '@atlassian/jira-navigation-apps-sidebar-nav4-projects-customise-order/src/ui/droppable-group-indicator/index.tsx';
import { useProjectsAnalyticsL2 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { STARRED_SECTION } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/project-content-section.tsx';
import messages from '../messages.tsx';
import type { FavoriteItemsType } from '../types.tsx';

const VIEW_ALL_STARRED_HREF = '/jira/your-work?starred';

export const RenderStarredSection = componentWithCondition(
	() => expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
	StarredSectionWithFocusRestoration,
	StarredSection,
);

function StarredSectionWithFocusRestoration({
	favoriteItems,
	canCreateProject,
}: {
	favoriteItems: FavoriteItemsType;
	canCreateProject: boolean | undefined | null;
}) {
	const { formatMessage } = useIntl();
	const { sendProjectMenuContentSectionAnalyticsEvent } = useProjectsAnalyticsL2();

	useEffect(() => {
		sendProjectMenuContentSectionAnalyticsEvent({
			projectSection: STARRED_SECTION,
			canCreateProject,
		});
	}, [canCreateProject, sendProjectMenuContentSectionAnalyticsEvent]);

	return (
		favoriteItems.hasItems && (
			<ProjectsDroppableGroupIndicator>
				<MenuListItem testId="navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.ui.content.starred-section.starred-section">
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.starred)}</MenuSectionHeading>
						<MenuList>{favoriteItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
				{favoriteItems.hasNextPage && (
					<Nav4MenuLinkItem href={VIEW_ALL_STARRED_HREF} menuId={MENU_ID_PROJECTS_VIEW_ALL_STARRED}>
						{formatMessage(messages.viewAllStarred)}
					</Nav4MenuLinkItem>
				)}
			</ProjectsDroppableGroupIndicator>
		)
	);
}

function StarredSection({
	favoriteItems,
	canCreateProject,
}: {
	favoriteItems: FavoriteItemsType;
	canCreateProject: boolean | undefined | null;
}) {
	const { formatMessage } = useIntl();
	const { sendProjectMenuContentSectionAnalyticsEvent } = useProjectsAnalyticsL2();

	useEffect(() => {
		sendProjectMenuContentSectionAnalyticsEvent({
			projectSection: STARRED_SECTION,
			canCreateProject,
		});
	}, [canCreateProject, sendProjectMenuContentSectionAnalyticsEvent]);

	return (
		favoriteItems.hasItems && (
			<>
				<MenuListItem testId="navigation-apps-sidebar-nav4-sidebars-content-projects-core.common.ui.content.starred-section.starred-section">
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.starred)}</MenuSectionHeading>
						<MenuList>{favoriteItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
				{favoriteItems.hasNextPage && (
					<Nav4MenuLinkItem href={VIEW_ALL_STARRED_HREF} menuId={MENU_ID_PROJECTS_VIEW_ALL_STARRED}>
						{formatMessage(messages.viewAllStarred)}
					</Nav4MenuLinkItem>
				)}
			</>
		)
	);
}
